import { boltApi } from 'store/queries/bolt-api'
import {
  GetLastMeterReadingResponseData,
  GetMeterReadingsPayload,
  GetMeterReadingsResponse,
  GetPreviousBillingCyclePayload,
  GetVolumesPayload,
  GetVolumesResponse
} from './types.ts'
import { convertToFullPeriod, parseBelgianTime } from 'utils/date.ts'
import { VolumeEntry } from 'pages/app/consumption/your-consumption/components/consumption-section/types.ts'
import { Response } from 'types/request'
import { GetPreviousBillingCyclePeriodsResponseData, PreviousBillingCyclePeriod } from 'types/billShock.ts'
import { Flow } from 'types/logging.ts'
import { TagTypes } from 'store/queries/bolt-api/types.ts'
import { PreviousBillingCycle } from 'pages/app/billing/billing-cycles/types.ts'
import { stringify } from 'qs'
import { createErrorTransformer } from 'store/queries/utils.ts'

export const contractsBoltApi = boltApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET PREVIOUS BILLING CYCLE
    getPreviousBillingCycle: builder.query<PreviousBillingCycle | null, GetPreviousBillingCyclePayload>({
      query: ({ billingContractId, billingPeriodId }) => `/contracts/${billingContractId}/billed-periods/${billingPeriodId}`,
      transformResponse: async ({ success, data }: Response<{ period: PreviousBillingCycle } | null>) => {
        if (success && data) return data.period
        else return null
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTRACTS}:getPreviousBillingCycle]`)
    }),

    // GET PREVIOUS BILLING CYCLE PERIODS
    getPreviousBillingCyclePeriods: builder.query<PreviousBillingCyclePeriod[], string>({
      query: (billingContractId) => `/contracts/${billingContractId}/billed-periods`,
      transformResponse: async ({ success, data }: Response<GetPreviousBillingCyclePeriodsResponseData>) => {
        if (success && data?.periods) return data.periods
        else return []
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTRACTS}:getPreviousBillingCyclePeriods]`)
    }),

    // GET VOLUMES
    getVolumes: builder.query<GetVolumesResponse, GetVolumesPayload>({
      query: ({ serviceContractId, granularity, from, until }) => ({
        url: `/contracts/${serviceContractId}/volumes?granularity=${granularity}&from=${from}&until=${until}`
      }),
      transformResponse: async ({ success, data, message }: Response<GetVolumesResponse>, _meta: unknown, arg: GetVolumesPayload) => {
        if (!success) {
          throw Error(message || 'get volumes failed')
        }

        return {
          nonSmr3VolumesAvailable: !!data?.nonSmr3VolumesAvailable,
          totalConsumption: data?.totalConsumption || 0,
          totalInjection: data?.totalInjection || 0,
          volumes: data?.volumes
            ? convertToFullPeriod<VolumeEntry>({
                data: data.volumes,
                from: parseBelgianTime(arg.from),
                until: parseBelgianTime(arg.until),
                granularity: arg.granularity,
                getDate: ({ datetime }) => datetime,
                createEmptyEntry: (datetime) => ({ datetime, consumption: { billed: 0 } })
              })
            : []
        }
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.CONTRACTS}:getVolumes]`),
      providesTags: [TagTypes.CONSUMPTION]
    }),

    // METER READINGS
    getMeterReadings: builder.query<GetMeterReadingsResponse['meterReadings'], GetMeterReadingsPayload>({
      async queryFn({ electricityContractNumber, gasContractNumber, onlyProvidedByDgo }, _queryApi, _extraOptions, fetchWithBQ) {
        const query = stringify({ onlyProvidedByDgo }, { skipNulls: true })

        // Fetch both electricity and gas (if available) in parallel
        const [electricityRes, gasRes] = await Promise.all([
          electricityContractNumber ? fetchWithBQ(`/contracts/${electricityContractNumber}/last-meter-reading?${query}`) : null,
          gasContractNumber ? fetchWithBQ(`/contracts/${gasContractNumber}/last-meter-reading?${query}`) : null
        ])

        if (electricityRes?.error || gasRes?.error) {
          return { error: (electricityRes?.error || gasRes?.error)! }
        }

        const electricity = electricityRes?.data ? (electricityRes?.data as Response<GetLastMeterReadingResponseData>) : undefined
        const gas = gasRes?.data ? (gasRes.data as Response<GetLastMeterReadingResponseData>) : undefined

        return {
          data: {
            electricity: electricity ? electricity?.data?.lastMeterReading || null : undefined,
            gas: gas ? gas?.data?.lastMeterReading || null : undefined
          }
        }
      },
      providesTags: [TagTypes.METER_READINGS],
      keepUnusedDataFor: 60 * 5 // keep in cache for 5 minutes (average session duration is 5mins 38secs - Posthog)
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetMeterReadingsQuery, useGetPreviousBillingCyclePeriodsQuery, useGetPreviousBillingCycleQuery, useGetVolumesQuery } =
  contractsBoltApi
